
import { useState } from "react";
import { Guest } from "@/types";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { TimeSlotList } from "@/components/booking/TimeSlotList";
import { GuestForm } from "@/components/booking/GuestForm";
import { BookingsList } from "@/components/booking/BookingsList";

const Index = () => {
  const queryClient = useQueryClient();
  const [guests, setGuests] = useState<Guest[]>([]);
  const [guestName, setGuestName] = useState("");
  const [skillLevel, setSkillLevel] = useState("");
  const [gender, setGender] = useState("");
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<string | null>(null);

  const { data: userProfile } = useQuery({
    queryKey: ['userProfile'],
    queryFn: async () => {
      const { data: session } = await supabase.auth.getSession();
      if (!session.session) return null;

      const { data, error } = await supabase
        .from('profiles_pending')
        .select('*')
        .eq('id', session.session.user.id)
        .maybeSingle();

      if (error) {
        console.error('Error fetching user profile:', error);
        return null;
      }

      return data;
    },
    retry: false,
  });

  const handleAddGuest = () => {
    if (!guestName || !skillLevel || !gender) return;

    const newGuest: Guest = {
      id: Math.random().toString(),
      name: guestName,
      skillLevel: skillLevel as Guest["skillLevel"],
      gender: gender as Guest["gender"],
    };

    setGuests([...guests, newGuest]);
    setGuestName("");
    setSkillLevel("");
    setGender("");
  };

  const handleTimeSlotClick = (slotId: string) => {
    setSelectedTimeSlot(selectedTimeSlot === slotId ? null : slotId);
  };

  const handleJoin = async () => {
    const { data: session } = await supabase.auth.getSession();
    if (!session.session) {
      toast.error("Please sign in to join the game");
      return;
    }

    if (!selectedTimeSlot) {
      toast.error("Please select a time slot");
      return;
    }

    try {
      // First fetch the time slot details
      const { data: timeSlot, error: timeSlotError } = await supabase
        .from('time_slots_to_select')
        .select('*, created_time_slot_id')
        .eq('id', selectedTimeSlot)
        .maybeSingle();

      if (timeSlotError) {
        console.error('Error fetching time slot:', timeSlotError);
        toast.error("Error fetching time slot details");
        return;
      }

      if (!timeSlot) {
        toast.error("Time slot not found or no longer available");
        queryClient.invalidateQueries({ queryKey: ['timeSlotsToSelect'] });
        setSelectedTimeSlot(null);
        return;
      }

      // Check if time slot is still available
      if (timeSlot.booked_players >= timeSlot.max_players) {
        toast.error("This time slot is no longer available");
        queryClient.invalidateQueries({ queryKey: ['timeSlotsToSelect'] });
        setSelectedTimeSlot(null);
        return;
      }

      // Prepare guests JSON
      const guestsJson = guests.map(guest => ({
        id: guest.id,
        name: guest.name,
        skillLevel: guest.skillLevel,
        gender: guest.gender
      }));

      // Create the booking using the created_time_slot_id
      const { error: bookingError } = await supabase
        .from('bookings')
        .insert({
          time_slot_id: timeSlot.created_time_slot_id,
          guest_count: guests.length,
          guests: guestsJson,
          amount: timeSlot.fee * (1 + guests.length),
          user_id: session.session.user.id,
          status: 'pending payment' // Set the initial status explicitly
        });

      if (bookingError) {
        console.error('Error creating booking:', bookingError);
        throw bookingError;
      }

      // Reset form and invalidate queries to refresh data
      setGuests([]);
      setSelectedTimeSlot(null);
      queryClient.invalidateQueries({ queryKey: ['userBookings'] });
      queryClient.invalidateQueries({ queryKey: ['timeSlotsToSelect'] });
      
      toast.success("Successfully joined the game!");

    } catch (error: any) {
      console.error('Error in join process:', error);
      toast.error(error.message || "Failed to join the game. Please try again.");
    }
  };

  return (
    <div className="container mx-auto py-8 animate-fadeIn">
      <h1 className="text-3xl font-bold mb-2">
        Hey {userProfile?.full_name?.split(' ')[0] || 'Player'}! 👋
      </h1>
      <p className="text-gray-600 mb-8">Welcome to Volleyball game registration</p>

      <div className="grid md:grid-cols-2 gap-8">
        <TimeSlotList
          selectedTimeSlot={selectedTimeSlot}
          onTimeSlotClick={handleTimeSlotClick}
        />
        <GuestForm
          guests={guests}
          guestName={guestName}
          skillLevel={skillLevel}
          gender={gender}
          selectedTimeSlot={selectedTimeSlot}
          onGuestNameChange={setGuestName}
          onSkillLevelChange={setSkillLevel}
          onGenderChange={setGender}
          onAddGuest={handleAddGuest}
          onJoin={handleJoin}
        />
      </div>

      <BookingsList />
    </div>
  );
};

export default Index;
